import { Member, MemberUtils } from "@shared/member.utils";
import type { Season as SeasonRow } from "../../stores/season.store";

/**
 * What the current account is allowed to do in the shop.
 *
 * - `unknown`: the account is authenticated but its profile is still loading;
 * - `visitor`: not authenticated, can browse only;
 * - `registered`: authenticated without a valid annual membership, can browse only;
 * - `member`: authenticated with a valid annual membership, can select and order.
 */
export type ShopAccess = "unknown" | "visitor" | "registered" | "member";

type ShopAccessInput = {
  isAuthenticated: boolean;
  // Null while the profile of an authenticated account is still loading.
  members: Member[] | null;
  // The season row carries the validity dates that the shared Season type omits.
  currentSeason: SeasonRow | null;
  now: Date;
};

// Membership validity follows the season dates, never the calendar year. The
// season boundaries are whole days, so the comparison is on days too and the
// last day of the season still counts.
const isSeasonCurrent = (season: SeasonRow, now: Date) => {
  const today = now.toISOString().slice(0, 10);

  return season.start_date <= today && today <= season.end_date;
};

export const resolveShopAccess = ({
  isAuthenticated,
  members,
  currentSeason,
  now,
}: ShopAccessInput): ShopAccess => {
  if (!isAuthenticated) {
    return "visitor";
  }

  if (!members) {
    return "unknown";
  }

  if (!currentSeason || !isSeasonCurrent(currentSeason, now)) {
    return "registered";
  }

  const hasValidMembership = members.some((member) => {
    return MemberUtils.getValidSubscription(member, currentSeason) !== null;
  });

  return hasValidMembership ? "member" : "registered";
};
